import {useState, useCallback} from 'react';
import {FormattedMessage} from 'react-intl';
import {useMutation, type ApolloCache, type Reference} from '@apollo/client';
import {DELETE_BOOKMARKS_MUTATION, type DeleteBookmarksOutput} from 'graphql/admin/bookmarks';
import {getErrorMessage} from 'utils';
import {useSnackbar} from 'hooks';
import {BookmarkTypename} from 'types';
import {DeleteIconButton, type DeleteIconButtonProps} from 'components/buttons';
import {useSelectedLinkContext} from 'contexts/SelectedLinkContext';
import DeleteBookmarkDialog from './DeleteBookmarkDialog';

const updateBookmarksCache = (cache : ApolloCache<any>, {data} : {data : DeleteBookmarksOutput}) => {
  const deletedBookmarkKey = data?.deleteBookmarks?.successes?.[0];
  if (deletedBookmarkKey) {
    cache.modify({
      fields: {
        getBookmarks({bookmarks: existingBookmarksRefs = [], ...getBookmarks}) {
          const deletedBookmarkCacheRef = cache.identify({__typename: BookmarkTypename, key: deletedBookmarkKey});
          return {
            ...getBookmarks,
            bookmarks: existingBookmarksRefs.filter((bookmarkRef : Reference) => bookmarkRef.__ref !== deletedBookmarkCacheRef)
          }
        }
      }
    });
  }
};

type DeleteBookmarkIconButtonProps = DeleteIconButtonProps & {
  bookmarkName: string;
  bookmarkKey: string;
}

const DeleteBookmarkIconButton = ({bookmarkName, bookmarkKey} : DeleteBookmarkIconButtonProps) : JSX.Element => {
  const [open, setOpen] = useState(false);

  const {selectedLink: {channelKey: channel}} = useSelectedLinkContext();
  const {successSnackbar} = useSnackbar();

  const onCompleted = (data : DeleteBookmarksOutput) => {
    const deletedBookmarkKey = data?.deleteBookmarks?.successes?.[0];
    if (deletedBookmarkKey) {
      successSnackbar(<FormattedMessage id="admin.bookmarks.delete.success" values={{ bookmarkName }} />);
    }
  }

  const [deleteBookmark, {loading, data, error, reset}] = useMutation<DeleteBookmarksOutput>(DELETE_BOOKMARKS_MUTATION, { update: updateBookmarksCache, onCompleted });
  
  const handleClick = () => setOpen(true);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const handleDeleteBookmark = () => deleteBookmark({variables: {deleteBookmarksInput: {channel, bookmarkKeys: [bookmarkKey]}}});

  return <>
    <DeleteIconButton
      labelId='admin.bookmarks.button.deleteBookmark'
      className='bg-admin-red-400 hover:bg-admin-red-500'
      onClick={handleClick}
    />
    <DeleteBookmarkDialog
      errorReason={data?.deleteBookmarks?.failures?.[0]?.reason || getErrorMessage(error)}
      bookmarkName={bookmarkName}
      open={open}
      onClose={handleClose}
      submitting={loading}
      handleDeleteBookmark={handleDeleteBookmark}
    />
  </>
}

export default DeleteBookmarkIconButton;